import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import pdfWorkerSrc from 'pdfjs-dist/build/pdf.worker.min.mjs' with { type: 'file' }
import fromPairs from 'lodash/fromPairs'
import groupBy from 'lodash/groupBy'
import isArray from 'lodash/isArray'
import isEqual from 'lodash/isEqual'
import isNumber from 'lodash/isNumber'
import isString from 'lodash/isString'
import times from 'lodash/times'
import { Skeleton } from '@klear/ui/components/skeleton'
import { cn } from '@klear/ui/lib/utils'
import { highlightFill } from '@/app/document-review/highlight-fill'
import { jumpTarget, type OnPagePlacement } from '@/app/document-review/jump-target'
import {
  foldPage,
  matchQuote,
  type FoldedPage,
  type PdfTextItem,
  type QuoteBox
} from '@/app/document-review/quote-positions'

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerSrc

export type QuoteHighlight = {
  key: string
  page: number
  rank: number
  quote: string
  confidence: string
}

export type PdfDocumentViewProps = {
  url: string
  highlights: readonly QuoteHighlight[]
  activeKey: string | null
  onLoadError: () => void
}

type PageLayout = { folded: FoldedPage; transform: readonly number[] }

type DrawnBox = { key: string; rank: number; doubted: boolean; guessed: boolean; box: QuoteBox }

type PagePlacements = Readonly<Record<string, OnPagePlacement>>

export const PDF_OPTIONS = { withCredentials: true }
const NO_HIGHLIGHTS: QuoteHighlight[] = []
const UNREADABLE_PAGE: PageLayout = { folded: foldPage([]), transform: [] }
const PAGES_AT_FIRST_PAINT = 3
const PAGES_PER_STEP = 3
const PAGE_GUTTER = 28
const SMALLEST_PAGE_WIDTH = 280
const WIDEST_PAGE_WIDTH = 1000
const LETTER_PAGE_ASPECT = 11 / 8.5

function textItems(items: readonly object[]): PdfTextItem[] {
  return items.flatMap((entry) => {
    const candidate = entry as Partial<PdfTextItem>
    if (!isString(candidate.str) || !isArray(candidate.transform) || !isNumber(candidate.width)) return []
    return [{ str: candidate.str, transform: candidate.transform, width: candidate.width }]
  })
}

function sourceId(key: string, rank: number): string {
  return `${key} ${rank}`
}

function prefersReducedMotion(): boolean {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true
}

export function PdfDocumentView({ url, highlights, activeKey, onLoadError }: PdfDocumentViewProps) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const sentinelRef = useRef<HTMLDivElement>(null)
  const [pageWidth, setPageWidth] = useState<number | null>(null)
  const [pageCount, setPageCount] = useState(0)
  const [drawnPages, setDrawnPages] = useState(PAGES_AT_FIRST_PAINT)

  const [placements, setPlacements] = useState<Record<number, PagePlacements>>({})

  const recordPlacements = useCallback(
    (page: number, onPage: PagePlacements) =>
      setPlacements((current) => (isEqual(current[page], onPage) ? current : { ...current, [page]: onPage })),
    []
  )

  const byPage = useMemo(() => groupBy(highlights, (highlight) => highlight.page), [highlights])

  const target = useMemo(() => {
    const candidates = highlights.filter((highlight) => highlight.key === activeKey)
    return jumpTarget(candidates, ({ page, rank }) => {
      if (pageCount > 0 && page > pageCount) return 'missing'
      const onPage = placements[page]
      if (onPage === undefined) return 'unread'
      return onPage[sourceId(activeKey ?? '', rank)] ?? 'missing'
    })
  }, [activeKey, highlights, pageCount, placements])

  useEffect(() => {
    const node = scrollRef.current
    if (node === null) return
    const measure = () =>
      setPageWidth(Math.min(WIDEST_PAGE_WIDTH, Math.max(SMALLEST_PAGE_WIDTH, node.clientWidth - PAGE_GUTTER)))
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const node = sentinelRef.current
    const root = scrollRef.current
    if (node === null || root === null) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) setDrawnPages((drawn) => drawn + PAGES_PER_STEP)
      },
      { root, rootMargin: '600px' }
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [pageCount])

  useEffect(() => {
    if (target !== null) setDrawnPages((drawn) => Math.max(drawn, target.page))
  }, [target])

  const shown = Math.min(drawnPages, pageCount)
  const skeletonHeight = pageWidth === null ? undefined : pageWidth * LETTER_PAGE_ASPECT

  return (
    <div ref={scrollRef} className="bg-muted/40 h-full overflow-auto p-3">
      <Document
        file={url}
        options={PDF_OPTIONS}
        onLoadSuccess={({ numPages }) => setPageCount(numPages)}
        onLoadError={onLoadError}
        loading={<Skeleton className="w-full" style={{ height: skeletonHeight }} />}
        error={<Skeleton className="w-full" style={{ height: skeletonHeight }} />}
        className="flex flex-col items-center gap-3"
      >
        {pageWidth !== null &&
          times(shown, (index) => (
            <HighlightedPage
              key={index + 1}
              pageNumber={index + 1}
              width={pageWidth}
              highlights={byPage[index + 1] ?? NO_HIGHLIGHTS}
              activeKey={activeKey}
              scrollRank={target?.page === index + 1 ? target.rank : null}
              onPlaced={recordPlacements}
            />
          ))}
        {shown < pageCount && <div ref={sentinelRef} className="h-4 w-full shrink-0" aria-hidden />}
      </Document>
    </div>
  )
}

type HighlightedPageProps = {
  pageNumber: number
  width: number
  highlights: readonly QuoteHighlight[]
  activeKey: string | null
  scrollRank: number | null
  onPlaced: (page: number, onPage: PagePlacements) => void
}

function HighlightedPage({ pageNumber, width, highlights, activeKey, scrollRank, onPlaced }: HighlightedPageProps) {
  const [layout, setLayout] = useState<PageLayout | null>(null)
  const activeRef = useRef<HTMLDivElement>(null)

  const boxes = useMemo((): DrawnBox[] => {
    if (layout === null) return []
    return highlights.flatMap((highlight) => {
      const match = matchQuote(layout.folded, layout.transform, highlight.quote)
      return match.boxes.map((box) => ({
        key: highlight.key,
        rank: highlight.rank,
        doubted: highlight.confidence === 'low',
        guessed: match.guessed,
        box
      }))
    })
  }, [highlights, layout])

  useEffect(() => {
    if (layout === null) return
    const onPage: PagePlacements = fromPairs(
      boxes.map((drawn): [string, OnPagePlacement] => [
        sourceId(drawn.key, drawn.rank),
        drawn.guessed ? 'guessed' : 'placed'
      ])
    )
    onPlaced(pageNumber, onPage)
  }, [boxes, layout, onPlaced, pageNumber])

  const scrollIndex =
    scrollRank === null ? -1 : boxes.findIndex((drawn) => drawn.key === activeKey && drawn.rank === scrollRank)

  useEffect(() => {
    if (scrollIndex === -1) return
    activeRef.current?.scrollIntoView({
      block: 'nearest',
      inline: 'nearest',
      behavior: prefersReducedMotion() ? 'auto' : 'smooth'
    })
  }, [scrollIndex, activeKey])

  return (
    <div className="relative shrink-0 bg-white shadow-sm" style={{ width }}>
      <Page
        pageNumber={pageNumber}
        width={width}
        renderTextLayer={false}
        renderAnnotationLayer={false}
        loading={<Skeleton style={{ width, height: width * LETTER_PAGE_ASPECT }} />}
        onLoadSuccess={(page) => {
          if (page.originalWidth <= 0) {
            setLayout(UNREADABLE_PAGE)
            return
          }
          const transform = page.getViewport({ scale: page.width / page.originalWidth }).transform
          page
            .getTextContent()
            .then((content) => setLayout({ folded: foldPage(textItems(content.items)), transform }))
            .catch(() => setLayout(UNREADABLE_PAGE))
        }}
      />
      <div className="pointer-events-none absolute inset-0">
        {boxes.map((drawn, index) => {
          const active = drawn.key === activeKey
          const dimmed = activeKey !== null && !active
          return (
            <div
              key={`${drawn.key}-${index}`}
              ref={index === scrollIndex ? activeRef : null}
              style={{ left: drawn.box.left, top: drawn.box.top, width: drawn.box.width, height: drawn.box.height }}
              className={cn(
                'absolute scroll-my-24 rounded-[2px] mix-blend-multiply transition-colors duration-150',
                highlightFill({ doubted: drawn.doubted || drawn.guessed, active, dimmed }),
                active && 'ring-1 ring-neutral-800'
              )}
            />
          )
        })}
      </div>
    </div>
  )
}
